/**
 * ModuleResultsTab Component
 * 
 * Shows findings from analysis runs with severity breakdown.
 * Includes "Generate Report" button that navigates to Report tab.
 * Displays analysis summary with positive assessments.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, AlertTriangle, FileText, TrendingUp, Play, Sparkles, Table } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ModuleAnalysesTab } from '@/components/ModuleAnalysesTab';
import { AnalysisSummaryCard } from '@/components/AnalysisSummaryCard';
import { ExtractedDataModal } from '@/components/ExtractedDataModal';
import { supabase } from '@/integrations/supabase/client';

interface FindingSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface DataSummary {
  row_count?: number;
  date_range?: { start?: string; end?: string };
  categories_detected?: string[];
  total_inflow?: number;
  total_outflow?: number;
}

interface PositiveAssessment {
  pattern: string;
  description: string;
}

interface ModuleResultsTabProps {
  auditId: string;
  moduleId: string;
  findingSummary?: FindingSummary;
  hasRuns: boolean;
  onNavigateToRun: () => void;
  onGenerateReport?: () => Promise<void>;
  onNavigateToReport?: () => void;
  isGeneratingReport?: boolean;
  hasExistingReport?: boolean;
}

export function ModuleResultsTab({
  auditId,
  moduleId,
  findingSummary,
  hasRuns,
  onNavigateToRun,
  onGenerateReport,
  onNavigateToReport,
  isGeneratingReport,
  hasExistingReport,
}: ModuleResultsTabProps) {
  const { t } = useTranslation();
  const [dataSummary, setDataSummary] = useState<DataSummary | null>(null);
  const [positiveAssessments, setPositiveAssessments] = useState<PositiveAssessment[]>([]);
  const [latestRevisionId, setLatestRevisionId] = useState<string | null>(null);
  const [showExtractedData, setShowExtractedData] = useState(false);

  // Fetch analysis summary from aggregates
  useEffect(() => {
    const fetchAnalysisSummary = async () => {
      if (!auditId || !moduleId) return;

      try {
        // Get module aliases for backward compatibility
        const { getModuleAliases } = await import('@/lib/modules');
        const moduleAliases = getModuleAliases(moduleId);
        const moduleTypeFilter = moduleAliases.map(a => `module_type.eq.${a}`).join(',');

        // Get latest completed revision
        const { data: revisions } = await supabase
          .from('revisions')
          .select('id')
          .eq('audit_id', auditId)
          .or(moduleTypeFilter)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1);

        if (!revisions || revisions.length === 0) return;

        const revisionId = revisions[0].id;
        setLatestRevisionId(revisionId);

        // Get aggregates with summary_json
        const { data: aggregates } = await supabase
          .from('aggregates')
          .select('summary_json')
          .eq('revision_id', revisionId)
          .maybeSingle();

        if (aggregates?.summary_json) {
          const summaryJson = aggregates.summary_json as any;
          if (summaryJson.data_summary) {
            setDataSummary(summaryJson.data_summary);
          }
          if (summaryJson.positive_assessments && Array.isArray(summaryJson.positive_assessments)) {
            setPositiveAssessments(summaryJson.positive_assessments);
          }
        }
      } catch (error) {
        console.error('[ModuleResultsTab] Error fetching analysis summary:', error);
      }
    };

    if (hasRuns) {
      fetchAnalysisSummary();
    }
  }, [auditId, moduleId, hasRuns]);

  const handleGenerateAndNavigate = async () => {
    if (onGenerateReport) {
      await onGenerateReport();
    }
    if (onNavigateToReport) {
      onNavigateToReport();
    }
  };

  if (!hasRuns) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="p-4 rounded-full bg-muted">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium">Inga resultat ännu</p>
              <p className="text-sm text-muted-foreground mt-1">
                Kör en analys för att se resultat här
              </p>
            </div>
            <Button onClick={onNavigateToRun} className="gap-2">
              <Play className="h-4 w-4" />
              Gå till Kör-fliken
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Analysis Summary Card - Always show when we have data */}
      {(dataSummary || positiveAssessments.length > 0 || (findingSummary && findingSummary.total === 0)) && (
        <AnalysisSummaryCard
          dataSummary={dataSummary}
          positiveAssessments={positiveAssessments}
          findingsCount={findingSummary?.total ?? 0}
        />
      )}

      {/* View Extracted Data Button */}
      {latestRevisionId && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowExtractedData(true)}
            className="gap-2"
          >
            <Table className="h-4 w-4" />
            Visa extraherad data
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      {findingSummary && findingSummary.total > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Totalt</p>
                  <p className="text-2xl font-bold">{findingSummary.total}</p>
                </div>
                <TrendingUp className="h-6 w-6 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-destructive/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-destructive">Kritisk</p>
                  <p className="text-2xl font-bold text-destructive">{findingSummary.critical}</p>
                </div>
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-orange-600">Hög</p>
                  <p className="text-2xl font-bold text-orange-600">{findingSummary.high}</p>
                </div>
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-warning/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-warning">Medel</p>
                  <p className="text-2xl font-bold text-warning">{findingSummary.medium}</p>
                </div>
                <AlertCircle className="h-6 w-6 text-warning" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-success/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-success">Låg</p>
                  <p className="text-2xl font-bold text-success">{findingSummary.low}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-success" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Generate Report Button - Always show if onGenerateReport is provided */}
      {onGenerateReport && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Generera analysrapport</p>
                <p className="text-sm text-muted-foreground">
                  {hasExistingReport 
                    ? 'En rapport finns redan. Du kan visa den eller generera en ny.'
                    : findingSummary && findingSummary.total > 0
                      ? `Sammanställ ${findingSummary.total} fynd till en professionell rapport`
                      : 'Generera en rapport baserad på analysresultaten'
                  }
                </p>
              </div>
              <div className="flex gap-2">
                {hasExistingReport && onNavigateToReport && (
                  <Button 
                    variant="outline" 
                    onClick={onNavigateToReport}
                    className="gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Visa rapport
                  </Button>
                )}
                <Button 
                  onClick={handleGenerateAndNavigate}
                  disabled={isGeneratingReport}
                  className="gap-2"
                >
                  {isGeneratingReport ? (
                    <>
                      <Sparkles className="h-4 w-4 animate-pulse" />
                      Genererar...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      {hasExistingReport ? 'Ny rapport' : 'Generera rapport'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Findings - Reuse existing component */}
      <ModuleAnalysesTab 
        auditId={auditId}
        moduleId={moduleId}
        onStartAnalysis={onNavigateToRun}
      />

      {/* Extracted Data Modal */}
      {latestRevisionId && (
        <ExtractedDataModal
          isOpen={showExtractedData}
          onClose={() => setShowExtractedData(false)}
          revisionId={latestRevisionId}
          moduleId={moduleId}
        />
      )}
    </div>
  );
}
